'use client';

import { createContext, type ReactNode, useEffect, useRef, useState } from 'react';
import { Container } from '@/atoms/Container/Container';
import { Spinner } from '@/atoms/Spinner/Spinner';
import { db } from '@/database/franky/franky';
import { AppError } from '@/libs/error/error';
import { DatabaseErrorCode } from '@/libs/error/error.codes';
import { Err } from '@/libs/error/error.factories';
import { ErrorService } from '@/libs/error/error.types';
import { DatabaseErrorScreen } from '@/molecules/DatabaseErrorScreen/DatabaseErrorScreen';
import { type DatabaseContextType } from '@/providers/DatabaseProvider/DatabaseProvider.types';
import { useMigrationStore } from '@/stores/migration/migration.store';

export const DatabaseContext = createContext<DatabaseContextType>({
  isReady: false,
  error: null,
  retry: async () => {},
});

/**
 * DatabaseProvider initializes the Dexie database and blocks rendering
 * until the database is ready. This prevents race conditions where
 * components try to query the database before it's initialized.
 */
export function DatabaseProvider({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  // Guards against the 'close' event (manually deleteding indexedDB fires this event) that fires during recreateDatabase() → this.close().
  // Without this, the close handler would re-trigger initDatabase and cause an infinite loop.
  const isInitializingRef = useRef(false);

  const initDatabase = async ({ allowRecreate }: { allowRecreate: boolean }) => {
    isInitializingRef.current = true;
    try {
      setError(null);
      const { wasDbReset } = await db.initialize({ allowRecreate });
      if (wasDbReset) {
        useMigrationStore.getState().setWasDbReset(true);
      }
      setIsReady(true);
    } catch (err) {
      setIsReady(false);
      if (err instanceof AppError) {
        setError(err);
      } else {
        // If it's not our AppError, it's likely a critical error from Dexie or browser
        setError(
          Err.database(DatabaseErrorCode.INIT_FAILED, 'Unexpected error during database initialization', {
            service: ErrorService.Local,
            operation: 'initDatabase',
            cause: err,
          }),
        );
      }
    } finally {
      isInitializingRef.current = false;
    }
  };

  // The user's retry may recreate the database again; see handleUnexpectedClose for the one path that may not.
  const retry = () => initDatabase({ allowRecreate: true });

  useEffect(() => {
    initDatabase({ allowRecreate: true });

    // Re-initialize when the DB is unexpectedly closed (e.g. user deletes IndexedDB via devtools, or a
    // tab running another build recreated it). Skips expected closes during recreateDatabase() via the
    // isInitializingRef guard. A version mismatch found from here, after this page already recreated the
    // database once, is that other tab at work: `allowRecreate: false` surfaces it as VERSION_CONFLICT
    // instead of recreating the database back and forth between the two tabs (ADR-0019).
    const handleUnexpectedClose = () => {
      if (isInitializingRef.current) return;
      setIsReady(false);
      initDatabase({ allowRecreate: false });
    };

    db.on('close', handleUnexpectedClose);

    return () => {
      db.on('close').unsubscribe(handleUnexpectedClose);
    };
  }, []);

  // Gate what renders on the database state:
  // - error: block the (broken) app and show a recovery screen wired to retry()
  // - not ready: block rendering until initialization completes to avoid query race conditions
  // - ready: render the app
  let content: ReactNode;
  if (error) {
    content = (
      <DatabaseErrorScreen
        onRetry={retry}
        description={
          error.code === DatabaseErrorCode.VERSION_CONFLICT ? 'Close other Pubky tabs, then retry.' : undefined
        }
      />
    );
  } else if (!isReady) {
    content = (
      <Container overrideDefaults className="flex min-h-screen items-center justify-center">
        <Spinner />
      </Container>
    );
  } else {
    content = children;
  }

  return (
    <DatabaseContext.Provider
      value={{
        isReady,
        error,
        retry,
      }}
    >
      {content}
    </DatabaseContext.Provider>
  );
}
